import React, { useState, useRef, useCallback, useEffect } from "react";
import { type AgGrid, Button, Flex, Grid, Select, Text } from "@jsq/ds/core";
import { type JobType } from "~/graphql/supergraphTypes";
import { SPACING } from "~/styles/variables";
import { useCustomFields } from "../CustomFields/hooks/useCustomFields";
import { BulkUploadGraphqlDataSource } from "./bulkUploadGraphqlDataSource";
import { columnDefs } from "./columnDefs";
import { UploadModal } from "./components/UploadModal";
import { gridOptions } from "./gridOptions";
import { headerSection, pageContainer } from "./styles_vanilla.css";

const UPLOAD_TYPE_OPTIONS = [
  { value: "tag_custom_fields", label: "Tag custom fields to entities" },
];

const UPLOAD_TYPE_TO_JOB_TYPE: Record<string, JobType> = {
  tag_custom_fields: "DataVaultBulkImport",
};

const BulkUpload: React.FC = () => {
  const [uploadType, setUploadType] = useState<string>("tag_custom_fields");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [gridApi, setGridApi] = useState<AgGrid.GridApi | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const gridApiRef = useRef<AgGrid.GridApi | null>(null);

  const { rawExtensionFields, loading: extensionFieldsLoading } = useCustomFields();

  const handleGridReady = useCallback((event: AgGrid.GridReadyEvent) => {
    gridApiRef.current = event.api;
    setGridApi(event.api);
  }, []);

  const handleOpenUploadModal = useCallback(() => {
    setIsUploadModalOpen(true);
  }, []);

  const handleCloseUploadModal = useCallback(() => {
    setIsUploadModalOpen(false);
  }, []);

  const handleUploadSuccess = useCallback(() => {
    gridApiRef.current?.refreshServerSide({ purge: true });
  }, []);
  useEffect(() => {
    if (!gridApi) return;

    const jobType = UPLOAD_TYPE_TO_JOB_TYPE[uploadType];
    if (!jobType) return;

    const dataSource = new BulkUploadGraphqlDataSource(jobType);
    gridApi.setGridOption("serverSideDatasource", dataSource);
  }, [gridApi, uploadType]);

  const handleRefresh = useCallback(() => {
    if (!gridApi) return;

    setIsRefreshing(true);
    gridApi.paginationGoToFirstPage();
    gridApi.refreshServerSide({ purge: true });
    setIsRefreshing(false);
  }, [gridApi]);

  return (
    <div className={pageContainer}>
      <Flex flexDirection="column" gap={SPACING.MEDIUM}>
        <Flex alignItems="center" gap={SPACING.SMALL}>
          <Text variant="secondary-md">Upload type:</Text>
          <Select
            value={uploadType}
            onChange={(val) => setUploadType(String(val))}
            data-testid="upload-type-select"
          >
            <Select.Section>
              {UPLOAD_TYPE_OPTIONS.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
        </Flex>

        <Flex className={headerSection}>
          <Text variant="h2">Bulk upload</Text>
          <Flex gap={SPACING.SMALL}>
            <Button
              variant="neutral"
              icon="sync-alt"
              data-testid="refresh-btn"
              onClick={handleRefresh}
              isLoading={isRefreshing}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              icon="upload"
              onClick={handleOpenUploadModal}
              disabled={extensionFieldsLoading || rawExtensionFields?.length === 0}
              data-testid="upload-file-btn"
            >
              Upload file
            </Button>
          </Flex>
        </Flex>
      </Flex>

      <Grid columnDefs={columnDefs} gridOptions={gridOptions} onGridReady={handleGridReady} />

      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        extensionFields={rawExtensionFields || []}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
};

export default BulkUpload;
